import React, { useState, useEffect, useMemo } from 'react';
import { Users, Wallet, Target, Calendar } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import localApiAdapter from '../../core/services/localApiAdapter';
import ModuleAnalyticsSummaryPage from '../../shared/components/analytics/ModuleAnalyticsSummaryPage';
import { buildDashboardCharts } from '../../shared/components/dashboard/moduleDashboardCharts';
import { formatBrandMoney } from '../../shared/utils/brandCurrency';
import { estimateTurnoverCost, getNitaqatBand } from '../../shared/utils/hrSaudiDefaults';

const DEFAULT_DATA = {
  employees: { total: 0, turnoverRate: 0, departmentDistribution: [] },
  recruitment: { totalActive: 0 },
  training: { totalSessions: 0, totalCost: 0 },
  evaluations: { avgScore: 0 },
  leaves: { totalPending: 0 },
  payroll: {
    totalGross: 0,
    avgGross: 0,
    totalEmployerCost: 0,
    turnoverCostEstimate: 0,
    byDepartment: [],
  },
  saudization: { saudi: 0, expat: 0, saudiPct: 0, nitaqatBand: 'red' },
};

function mapHrDashboardResponse(raw) {
  if (!raw || typeof raw !== 'object') return DEFAULT_DATA;

  if (raw.employees || raw.payroll || raw.saudization) {
    return {
      ...DEFAULT_DATA,
      ...raw,
      employees: { ...DEFAULT_DATA.employees, ...(raw.employees || {}) },
      payroll: { ...DEFAULT_DATA.payroll, ...(raw.payroll || {}) },
      saudization: { ...DEFAULT_DATA.saudization, ...(raw.saudization || {}) },
      training: { ...DEFAULT_DATA.training, ...(raw.training || {}) },
      leaves: { ...DEFAULT_DATA.leaves, ...(raw.leaves || {}) },
      recruitment: { ...DEFAULT_DATA.recruitment, ...(raw.recruitment || {}) },
    };
  }

  const kpis = raw.kpis || {};
  const chartRows = Array.isArray(raw.chartRows) ? raw.chartRows : [];

  return {
    ...DEFAULT_DATA,
    employees: {
      ...DEFAULT_DATA.employees,
      total: kpis.totalEmployees ?? 0,
      departmentDistribution: chartRows.map((row) => ({
        department: row.label,
        count: Number(row.value) || 0,
      })),
    },
    training: {
      ...DEFAULT_DATA.training,
      totalSessions: kpis.trainingActive ?? raw.training?.totalSessions ?? 0,
    },
    leaves: {
      ...DEFAULT_DATA.leaves,
      totalPending: kpis.onLeaveToday ?? raw.leaves?.totalPending ?? 0,
    },
    recruitment: {
      ...DEFAULT_DATA.recruitment,
      totalActive: kpis.openPositions ?? raw.recruitment?.totalActive ?? 0,
    },
    saudization: raw.saudization || DEFAULT_DATA.saudization,
    payroll: raw.payroll || DEFAULT_DATA.payroll,
  };
}

const HrAnalysesRapportsPage = () => {
  const { t } = useTranslation();
  const p = (key, opts) => t(`modules.ressources_humaines.pages.hrAnalysesRapportsPage.${key}`, opts);
  const formatAmount = (amount) => formatBrandMoney(amount);

  const [report, setReport] = useState(DEFAULT_DATA);
  const [loading, setLoading] = useState(true);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const loadReport = async () => {
    setLoading(true);
    try {
      const dashboard = await localApiAdapter.get('/hr/dashboard');
      setReport(mapHrDashboardResponse(dashboard));
    } catch (error) {
      console.error('HR analyses load error:', error);
      setReport(DEFAULT_DATA);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadReport();
  }, [refreshTrigger]);

  const employees = report.employees || DEFAULT_DATA.employees;
  const recruitment = report.recruitment || DEFAULT_DATA.recruitment;
  const training = report.training || DEFAULT_DATA.training;
  const leaves = report.leaves || DEFAULT_DATA.leaves;
  const payroll = report.payroll || DEFAULT_DATA.payroll;
  const saudization = report.saudization || DEFAULT_DATA.saudization;
  const turnoverCost = payroll.turnoverCostEstimate || estimateTurnoverCost(payroll.avgGross);
  const nitaqatBand = saudization.nitaqatBand || getNitaqatBand(saudization.saudiPct);

  const insights = useMemo(
    () => [
      {
        label: p('insight_nitaqat_band'),
        value: loading ? '…' : p(`nitaqat_${nitaqatBand}`),
        hint: p('insight_nitaqat_hint', { pct: saudization.saudiPct || 0 }),
      },
      {
        label: p('insight_turnover_cost'),
        value: loading ? '…' : formatAmount(turnoverCost),
        hint: p('insight_turnover_hint'),
      },
      {
        label: p('insight_training_sessions'),
        value: loading ? '…' : String(training.totalSessions || 0),
        hint: p('insight_training_hint', { cost: formatAmount(training.totalCost || 0) }),
      },
    ],
    [loading, nitaqatBand, saudization.saudiPct, turnoverCost, training, p]
  );

  const stats = useMemo(
    () =>
      [
        { label: p('stat_employees'), value: loading ? '…' : employees.total || 0, icon: Users },
        { label: p('stat_payroll_cost'), value: loading ? '…' : formatAmount(payroll.totalGross || 0), icon: Wallet },
        { label: p('stat_saudization'), value: loading ? '…' : `${saudization.saudiPct || 0}%`, icon: Target },
        { label: p('stat_pending_leaves'), value: loading ? '…' : leaves.totalPending || 0, icon: Calendar },
      ].slice(0, 4),
    [loading, employees, recruitment, payroll, saudization, training, leaves, p]
  );

  const charts = useMemo(() => {
    const deptRows = (employees.departmentDistribution || []).slice(0, 8).map((row) => ({
      label: row.department || p('na'),
      value: row.count || 0,
      display: String(row.count || 0),
    }));

    const payrollRows = (payroll.byDepartment || []).slice(0, 8).map((row) => ({
      label: row.department || p('na'),
      value: row.grossPayroll || 0,
      display: formatAmount(row.grossPayroll || 0),
    }));

    return buildDashboardCharts({
      ranked: {
        title: p('chart_by_department'),
        subtitle: p('chart_by_department_subtitle'),
        alwaysShow: true,
        rows: deptRows,
      },
      status: {
        title: p('chart_saudization'),
        subtitle: p('chart_saudization_subtitle'),
        active: saudization.saudi || 0,
        inactive: saudization.expat || 0,
        alwaysShow: true,
      },
      pipeline: {
        title: p('chart_payroll_by_dept'),
        subtitle: p('chart_payroll_by_dept_subtitle'),
        alwaysShow: true,
        rows: payrollRows,
      },
    });
  }, [employees, saudization, payroll, p]);

  const table = useMemo(() => {
    const deptPayroll = payroll.byDepartment || [];
    if (deptPayroll.length) {
      return {
        title: p('table_payroll'),
        columns: [
          { key: 'department', label: p('col_department') },
          { key: 'count', label: p('col_count') },
          { key: 'grossPayroll', label: p('col_gross_payroll') },
          { key: 'employerCost', label: p('col_employer_cost') },
          { key: 'saudizationPct', label: p('col_saudization_pct') },
        ],
        rows: deptPayroll.map((row) => ({
          department: row.department || p('na'),
          count: row.count ?? 0,
          grossPayroll: formatAmount(row.grossPayroll),
          employerCost: formatAmount(row.employerCost),
          saudizationPct: row.saudizationPct != null ? `${row.saudizationPct}%` : '—',
        })),
      };
    }

    return {
      title: p('table_departments'),
      columns: [
        { key: 'department', label: p('col_department') },
        { key: 'count', label: p('col_count') },
        { key: 'note', label: p('stat_turnover_cost') },
      ],
      rows: (employees.departmentDistribution || []).map((row, idx) => ({
        department: row.department || p('na'),
        count: row.count ?? 0,
        note: idx === 0 ? formatAmount(turnoverCost) : '—',
      })),
    };
  }, [payroll, employees, turnoverCost, p]);

  return (
    <ModuleAnalyticsSummaryPage
      title={p('title')}
      subtitle={`${p('subtitle')} — ${p('currency_note')}`}
      loading={loading}
      onRefresh={() => setRefreshTrigger((n) => n + 1)}
      stats={stats}
      insights={insights}
      charts={charts}
      table={table}
      exportFilePrefix="hr_analyses"
      emptyLabel={p('no_data')}
    />
  );
};

export default HrAnalysesRapportsPage;
